'use client'

import React, { useState, useEffect, useRef } from 'react';
import ReactDOM from 'react-dom';
import { useGetProductCatalogMutation } from '@/shared/api-rtk';
import { TOAST_ERROR } from '@/shared/constants/toastTypes';
import { IMAGE_DIR } from '@/shared/constants/linksApi';
import './ProtocolsChapter.css';

// Компонент для превью PDF (первая страница)
const PDFThumbnail = ({ url }) => {
  const canvasRef = useRef(null);
  const [error, setError] = useState(false);

  useEffect(() => {
    let isMounted = true;
    
    const generateThumbnail = async () => {
      try {
        if (typeof window.pdfjsLib === 'undefined') {
          await loadScript('https://cdnjs.cloudflare.com/ajax/libs/pdf.js/2.16.105/pdf.min.js');
          window.pdfjsLib.GlobalWorkerOptions.workerSrc = 'https://cdnjs.cloudflare.com/ajax/libs/pdf.js/2.16.105/pdf.worker.min.js';
        }

        const loadingTask = window.pdfjsLib.getDocument(url);
        const pdf = await loadingTask.promise;
        
        if (!isMounted) return;

        const page = await pdf.getPage(1);
        
        const containerWidth = 200;
        const viewport = page.getViewport({ scale: 1 });
        const scale = containerWidth / viewport.width;
        const scaledViewport = page.getViewport({ scale });

        const canvas = canvasRef.current;
        if (!canvas) return;
        
        canvas.width = scaledViewport.width;
        canvas.height = scaledViewport.height;
        
        const context = canvas.getContext('2d');
        
        const renderContext = {
          canvasContext: context,
          viewport: scaledViewport
        };
        
        await page.render(renderContext).promise;
      } catch (err) {
        console.error('Error generating PDF thumbnail:', err);
        if (isMounted) {
          setError(true);
        }
      }
    };

    if (url) {
      generateThumbnail();
    }

    return () => {
      isMounted = false;
    };
  }, [url]);

  const loadScript = (src) => {
    return new Promise((resolve, reject) => {
      const script = document.createElement('script');
      script.src = src;
      script.onload = resolve;
      script.onerror = reject;
      document.head.appendChild(script);
    });
  };

  if (error) {
    return (
      <div className="pdf-thumbnail-error">
        <svg width="40" height="40" viewBox="0 0 24 24" fill="none" stroke="#dc3545" strokeWidth="1">
          <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" />
          <polyline points="14 2 14 8 20 8" />
          <line x1="16" y1="13" x2="8" y2="13" />
          <line x1="16" y1="17" x2="8" y2="17" />
          <polyline points="10 9 9 9 8 9" />
        </svg>
        <span>PDF</span>
      </div>
    );
  }

  return (
    <canvas 
      ref={canvasRef} 
      className="pdf-thumbnail-canvas"
      style={{
        width: '100%',
        height: '100%',
        objectFit: 'contain',
        background: '#f8f9fa'
      }}
    />
  );
};

// Компонент для ПК - используем iframe с зумом
const DesktopPDFViewer = ({ url, title }) => {
  const [scale, setScale] = useState(1);

  const zoomIn = () => {
    setScale(prev => Math.min(prev + 0.25, 3));
  };

  const zoomOut = () => {
    setScale(prev => Math.max(prev - 0.25, 0.5));
  };

  const resetZoom = () => {
    setScale(1);
  };

  return (
    <>
      <div 
        className="pdf-viewer-container"
        style={{
          width: '100%',
          height: '100%',
          overflow: 'auto',
          background: '#525659',
          position: 'relative',
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'flex-start'
        }}
      >
        <iframe
          src={`${url}#toolbar=0&navpanes=0&scrollbar=0&view=FitH`}
          className="pdf-iframe"
          title={title || "PDF документ"}
          style={{
            width: '100%',
            height: '100%',
            border: 'none',
            transform: `scale(${scale})`,
            transformOrigin: 'top center',
            transition: 'transform 0.1s ease'
          }}
        />
      </div>
      
      {/* Кнопки масштабирования только для ПК */}
      <div className="pdf-zoom-controls">
        <button 
          onClick={zoomOut}
          className="zoom-btn dark"
          title="Уменьшить"
          disabled={scale <= 0.5}
        >
          <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
            <circle cx="12" cy="12" r="10"/>
            <line x1="8" y1="12" x2="16" y2="12"/>
          </svg>
        </button>
        
        <button 
          onClick={resetZoom}
          className="zoom-btn dark zoom-percent"
          title="Сбросить масштаб (100%)"
        >
          {Math.round(scale * 100)}%
        </button>
        
        <button 
          onClick={zoomIn}
          className="zoom-btn dark"
          title="Увеличить"
          disabled={scale >= 3}
        >
          <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
            <circle cx="12" cy="12" r="10"/>
            <line x1="12" y1="8" x2="12" y2="16"/>
            <line x1="8" y1="12" x2="16" y2="12"/>
          </svg>
        </button>
      </div>
    </>
  );
};

// Компонент для мобильных - используем PDF.js (без зума)
const MobilePDFViewer = ({ url, title }) => {
  const containerRef = useRef(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    let isMounted = true;
    
    const loadPDF = async () => {
      try {
        if (typeof window.pdfjsLib === 'undefined') {
          await loadScript('https://cdnjs.cloudflare.com/ajax/libs/pdf.js/2.16.105/pdf.min.js');
          window.pdfjsLib.GlobalWorkerOptions.workerSrc = 'https://cdnjs.cloudflare.com/ajax/libs/pdf.js/2.16.105/pdf.worker.min.js';
        }

        const loadingTask = window.pdfjsLib.getDocument(url);
        const pdf = await loadingTask.promise;
        
        if (!isMounted) return;
        
        setLoading(false);
        
        if (containerRef.current) {
          containerRef.current.innerHTML = '';
        }

        for (let pageNum = 1; pageNum <= pdf.numPages; pageNum++) {
          const page = await pdf.getPage(pageNum);
          
          const containerWidth = containerRef.current?.clientWidth || window.innerWidth;
          const viewport = page.getViewport({ scale: 1.5 });
          
          const pageDiv = document.createElement('div');
          pageDiv.className = 'pdf-page';
          pageDiv.style.margin = '20px auto';
          pageDiv.style.boxShadow = '0 2px 8px rgba(0,0,0,0.1)';
          pageDiv.style.background = 'white';
          
          const canvas = document.createElement('canvas');
          canvas.className = 'pdf-canvas';
          const context = canvas.getContext('2d');
          
          canvas.width = viewport.width;
          canvas.height = viewport.height;
          
          pageDiv.appendChild(canvas);
          containerRef.current?.appendChild(pageDiv);
          
          const renderContext = {
            canvasContext: context,
            viewport: viewport
          };
          
          await page.render(renderContext).promise;
        }
      } catch (err) {
        console.error('Error loading PDF:', err);
        if (isMounted) {
          setError('Не удалось загрузить PDF файл');
          setLoading(false);
        }
      }
    };

    if (url) {
      loadPDF();
    }

    return () => {
      isMounted = false;
    };
  }, [url]);

  const loadScript = (src) => {
    return new Promise((resolve, reject) => {
      const script = document.createElement('script');
      script.src = src;
      script.onload = resolve;
      script.onerror = reject;
      document.head.appendChild(script);
    });
  };

  if (loading) {
    return (
      <div className="pdf-loading">
        <div className="spinner-border text-light" role="status">
          <span className="visually-hidden">Загрузка PDF...</span>
        </div>
        <p>Загрузка документа...</p>
      </div>
    );
  }

  if (error) {
    return (
      <div className="pdf-error">
        <p>{error}</p>
        <a 
          href={url} 
          target="_blank" 
          rel="noopener noreferrer"
          className="pdf-download-link"
        >
          Открыть PDF в новой вкладке
        </a>
      </div>
    );
  }

  return (
    <div 
      ref={containerRef} 
      className="pdf-viewer-container"
      style={{
        width: '100%',
        height: '100%',
        overflow: 'auto',
        background: '#525659',
        padding: '20px 0'
      }}
    />
  );
};

const ModalPortal = ({ children }) => {
  const [mounted, setMounted] = useState(false);

  useEffect(() => {
    setMounted(true);
    return () => setMounted(false);
  }, []);

  return mounted ? ReactDOM.createPortal(
    children,
    document.body
  ) : null;
};

const ProtocolViewerModal = ({ 
    isOpen, 
    onClose, 
    protocols, 
    selectedImageIndex
  }) => {
    const [isMobile, setIsMobile] = useState(false);

    useEffect(() => {
      const checkMobile = () => {
        setIsMobile(window.innerWidth <= 768);
      };
      checkMobile();
      window.addEventListener('resize', checkMobile);
      return () => window.removeEventListener('resize', checkMobile);
    }, []);

    useEffect(() => {
      if (isOpen) {
        const zoom = document.body.style.zoom;
        document.body.style.zoom = '1';
        
        return () => {
          document.body.style.zoom = zoom;
        };
      }
    }, [isOpen]);

    if (!isOpen) return null;

    const currentProtocol = protocols[selectedImageIndex];
    if (!currentProtocol) return null;

    const fileUrl = currentProtocol.file || currentProtocol.image;
    const isPdf = fileUrl && fileUrl.toLowerCase().endsWith('.pdf');
    const fullUrl = `${IMAGE_DIR}${fileUrl}`;

    return (
      <ModalPortal>
        <div className="pdf-fullscreen-overlay">
          <button 
            className="minimal-close-btn" 
            onClick={onClose}
            title="Закрыть (Esc)"
          >
            <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <line x1="18" y1="6" x2="6" y2="18"></line>
              <line x1="6" y1="6" x2="18" y2="18"></line>
            </svg>
          </button>

          <div className="pdf-content-wrapper">
            {isPdf ? (
              isMobile ? (
                <MobilePDFViewer 
                  url={fullUrl}
                  title={currentProtocol.title || `Документ ${selectedImageIndex + 1}`}
                />
              ) : (
                <DesktopPDFViewer 
                  url={fullUrl}
                  title={currentProtocol.title || `Документ ${selectedImageIndex + 1}`}
                />
              )
            ) : (
              <img
                src={fullUrl}
                alt={currentProtocol.title || `Изображение ${selectedImageIndex + 1}`}
                className="fullscreen-image"
                draggable="false"
              />
            )}
          </div>
        </div>
      </ModalPortal>
    );
  };

const ProtocolsChapter = ({ productId, productName }) => {
    const [protocols, setProtocols] = useState([]);
    const [selectedImageIndex, setSelectedImageIndex] = useState(null);
    const [currentPage, setCurrentPage] = useState(1);
    const [isMobile, setIsMobile] = useState(false);
    const [loading, setLoading] = useState(true);
    const getProduct = useGetProductCatalogMutation()[0];
    const [imagesPerRow, setImagesPerRow] = useState(6);

    // Определение мобильного устройства
    useEffect(() => {
        const checkMobile = () => {
            setIsMobile(window.innerWidth <= 768);
        };
        checkMobile();
        window.addEventListener('resize', checkMobile);
        return () => window.removeEventListener('resize', checkMobile);
    }, []);

    // Определение количества изображений в строке
    useEffect(() => {
        const updateImagesPerRow = () => {
            const width = window.innerWidth;
            if (width >= 1400) setImagesPerRow(6);
            else if (width >= 1200) setImagesPerRow(5);
            else if (width >= 992) setImagesPerRow(4);
            else if (width >= 768) setImagesPerRow(3);
            else if (width >= 576) setImagesPerRow(2);
            else setImagesPerRow(1);
        };
        updateImagesPerRow();
        window.addEventListener('resize', updateImagesPerRow);
        return () => window.removeEventListener('resize', updateImagesPerRow);
    }, []);

    // Загрузка данных
    useEffect(() => {
        setLoading(true);
        getProduct(productId)
            .unwrap()
            .then((res) => {
                if (res.result === 'success') {
                    const productData = Object.values(res.data)[0];
                    if (productData.protocols) {
                        const protocolsArray = Object.entries(productData.protocols)
                            .map(([key, protocol]) => ({
                                ...protocol,
                                id: extractNumericId(key, protocol.product_protocol_id),
                                originalKey: key
                            }))
                            .filter(protocol => protocol.file || protocol.image) 
                            .sort((a, b) => {
                                const idA = a.id;
                                const idB = b.id;
                                if (!isNaN(idA) && !isNaN(idB)) {
                                    return parseInt(idA) - parseInt(idB);
                                }
                                return String(a.originalKey || '').localeCompare(String(b.originalKey || ''));
                            });
                        
                        setProtocols(protocolsArray);
                    }
                }
            })
            .catch(() => TOAST_ERROR('Не удалось получить документы'))
            .finally(() => setLoading(false));
    }, [productId, getProduct]);

    const extractNumericId = (key, protocolId) => {
        const match = String(key).match(/(\d+)/);
        if (match && match[1]) return parseInt(match[1]);
        if (protocolId) {
            const protocolMatch = String(protocolId).match(/(\d+)/);
            if (protocolMatch && protocolMatch[1]) return parseInt(protocolMatch[1]);
        }
        return key;
    };

    const openSlider = (index) => {
        setSelectedImageIndex(index);
        document.body.style.overflow = 'hidden';
        document.body.style.position = 'fixed';
        document.body.style.width = '100%';
        document.body.style.height = '100%';
    };

    const closeSlider = () => {
        setSelectedImageIndex(null);
        document.body.style.overflow = 'auto';
        document.body.style.position = '';
        document.body.style.width = '';
        document.body.style.height = '';
    };

    // Обработчик клавиатуры (только Esc)
    useEffect(() => {
        const handleKeyDown = (e) => {
            if (selectedImageIndex !== null) {
                if (e.key === 'Escape') closeSlider();
            }
        };
        window.addEventListener('keydown', handleKeyDown);
        return () => window.removeEventListener('keydown', handleKeyDown);
    }, [selectedImageIndex]);

    // Пагинация
    const imagesPerPage = imagesPerRow * 2;
    const indexOfLastImage = currentPage * imagesPerPage;
    const indexOfFirstImage = indexOfLastImage - imagesPerPage;
    const currentImages = protocols.slice(indexOfFirstImage, indexOfLastImage);
    const totalPages = Math.ceil(protocols.length / imagesPerPage);

    const gridStyle = {
        '--images-per-row': imagesPerRow
    };

    if (loading) {
        return (
            <div className="protocols-loading">
                <div className="spinner-border text-primary" role="status">
                    <span className="visually-hidden">Загрузка...</span>
                </div>
                <p>Загрузка документов...</p>
            </div>
        );
    }

    if (!protocols || protocols.length === 0) {
        return (
            <div className="protocols-empty">
                <p>Нет доступных документов для этого товара</p>
            </div>
        );
    }

    return (
        <div className="protocols-chapter">
            <div className="protocols-header">
                <h3>Протоколы для товара: {productName}</h3>
                <p>Найдено протоколов: {protocols.length}</p>
            </div>

           {/* Галерея миниатюр */}
           <div className="images-gallery-grid" style={gridStyle}>
                {currentImages.map((protocol, index) => {
                    const fileUrl = protocol.file || protocol.image;
                    const isPdf = fileUrl && fileUrl.toLowerCase().endsWith('.pdf');
                    const fullUrl = `${IMAGE_DIR}${fileUrl}`;

                    return (
                        <div
                            key={`protocol-${protocol.id}-${protocol.originalKey}`}
                            className="image-gallery-item"
                            onClick={() => openSlider(indexOfFirstImage + index)}
                        >
                            <div className="image-thumbnail-container">
                                {!isPdf ? (
                                    <img
                                        src={fullUrl}
                                        alt={protocol.title}
                                        className="image-thumbnail"
                                        loading="lazy"
                                    />
                                ) : (
                                    <PDFThumbnail url={fullUrl} />
                                )}
                                <div className="image-overlay"></div>
                            </div>
                            <div className="image-info">
                                <h4>{protocol.title || `Протокол ${indexOfFirstImage + index + 1}`}</h4>
                                {protocol.description && <p>{protocol.description}</p>}
                            </div>
                        </div>
                    );
                })}
            </div>

            {/* Пагинация */}
            {totalPages > 1 && (
                <div className="pagination-controls">
                    <button 
                        className="pagination-btn"
                        onClick={() => setCurrentPage(prev => Math.max(prev - 1, 1))}
                        disabled={currentPage === 1}
                    >
                        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                            <polyline points="15 18 9 12 15 6"></polyline>
                        </svg>
                        Назад
                    </button>
                    
                    <div className="page-indicator">
                        Страница 
                        <select 
                            value={currentPage}
                            onChange={(e) => setCurrentPage(Number(e.target.value))}
                            className="page-select"
                        >
                            {Array.from({ length: totalPages }, (_, i) => i + 1).map(page => (
                                <option key={page} value={page}>{page}</option>
                            ))}
                        </select>
                        из {totalPages}
                    </div>
                    
                    <button 
                        className="pagination-btn"
                        onClick={() => setCurrentPage(prev => Math.min(prev + 1, totalPages))}
                        disabled={currentPage === totalPages}
                    >
                        Вперед
                        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                            <polyline points="9 18 15 12 9 6"></polyline>
                        </svg>
                    </button>
                </div>
            )}

            {/* Модальное окно просмотра PDF */}
            <ProtocolViewerModal
                isOpen={selectedImageIndex !== null}
                onClose={closeSlider}
                protocols={protocols}
                selectedImageIndex={selectedImageIndex}
            />
        </div>
    );
};

export default ProtocolsChapter;